/**
 * 应用布局壳。
 *
 * 顶栏常驻季度选择器 —— 用户在任何页面都知道自己在看哪一季，
 * 切季不需要回首页。这是「不打断心智」最基本的一条。
 */
import { useMemo, useState} from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Alert, Avatar, Button, Dropdown, Layout, Menu, Select, Space, Spin, Tag, Tooltip, Typography } from 'antd'
import { LogoutOutlined, UserOutlined } from '@ant-design/icons'
import { useAuth } from '@/context/AuthContext'
import { useSeason } from '@/context/SeasonContext'
import { ALL_SECTIONS, findNavItem, findNavSection } from '@/config/navigation'
import './AppLayout.css'
import NewSeasonDrawer from '@/pages/season/components/NewSeasonDrawer'

const { Header, Sider, Content } = Layout
const { Text } = Typography

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, hasModulePermission } = useAuth()
  const season = useSeason()

  const activeItem = findNavItem(location.pathname)
  const activeSection = findNavSection(location.pathname)

  const menuItems = useMemo(
    () =>
      ALL_SECTIONS.map((section) => {
        const children = section.items
          .filter((item) => {
            if (item.adminOnly) return Boolean(user?.is_admin)
            if (!item.requiredPermission) return true
            return hasModulePermission(item.requiredPermission)
          })
          .map((item) => ({
            key: item.path,
            label: <Link to={item.path}>{item.label}</Link>,
          }))

        if (section.key === 'workspace-section') return children[0]

        return {
          key: section.key,
          label: section.label,
          children,
          className: section.key === 'base' ? 'app-menu-base-section' : undefined,
        }
      }).filter((item) => item && (!('children' in item) || item.children.length > 0)),
    [user, hasModulePermission],
  )

  const [newSeasonOpen, setNewSeasonOpen] = useState(false)

  return (
    <Layout className="app-shell">
      <Header className="app-header">
        <div className="app-brand" onClick={() => navigate('/season')}>
          CBMS
        </div>

        {/* 全局锚点固定项目与工作季；计划仅表示当前页面执行上下文。 */}
        <Space size={8} className="app-scope">
          <Select
            size="small"
            style={{ minWidth: 150 }}
            value={season.project?.id}
            onChange={season.setProject}
            options={season.projects.map((p) => ({ value: p.id, label: p.project_name }))}
            placeholder="项目"
            loading={season.loading}
          />
          <Select
            size="small"
            style={{ minWidth: 140 }}
            value={season.cycle?.id}
            onChange={season.setCycle}
            options={season.cycles.map((c) => ({ value: c.id, label: c.cycle_name }))}
            placeholder="季"
            allowClear
          />
          <Select
            size="small"
            style={{ minWidth: 200 }}
            value={season.plan?.id}
            onChange={season.setPlan}
            options={season.plans.map((p) => ({ value: p.id, label: p.plan_name }))}
            placeholder="当前执行计划"
            allowClear
          />
          {/* 开季入口放在锚点旁边：用户脑子里「换季」和「开季」是同一件事 */}
          <Tooltip title="项目 / 周期 / 计划书 一次建齐">
            <Button size="small" type="dashed" onClick={() => setNewSeasonOpen(true)}>
              + 新建一季
            </Button>
          </Tooltip>
        </Space>

        <div className="app-header-right">
          <Dropdown
            menu={{
              items: [
                { key: 'profile', label: <Link to="/profile">个人设置</Link>, icon: <UserOutlined /> },
                { type: 'divider' },
                { key: 'logout', label: '退出登录', icon: <LogoutOutlined />, onClick: logout },
              ],
            }}
          >
            <Space size={6} className="app-user">
              <Avatar size={26} icon={<UserOutlined />} />
              <Text style={{ color: 'rgba(255,255,255,.85)' }}>
                {user?.full_name || user?.username}
              </Text>
            </Space>
          </Dropdown>
        </div>
      </Header>

      <Layout>
        <Sider width={188} className="app-sider" theme="light">
          <Menu
            mode="inline"
            selectedKeys={activeItem ? [activeItem.path] : []}
            defaultOpenKeys={activeSection ? [activeSection.key] : ['planning']}
            items={menuItems}
            inlineIndent={14}
            style={{ borderInlineEnd: 'none' }}
          />
        </Sider>

        <Content className="app-content">
          {activeItem && (
            <div className="page-head">
              <span className="page-title">{activeItem.label}</span>
              <span className="page-hint">{activeItem.hint}</span>
              {season.cycle && <Tag color="blue">{season.cycle.cycle_name}</Tag>}
            </div>
          )}

          {season.error && (
            <Alert type="warning" showIcon message={season.error} style={{ marginBottom: 12 }} />
          )}

          {season.loading && !season.project ? (
            <div className="page-loading">
              <Spin /><span style={{ marginLeft: 10, color: "rgba(0,0,0,.45)" }}>加载季度信息…</span>
            </div>
          ) : (
            children
          )}
        </Content>
      </Layout>
      <NewSeasonDrawer open={newSeasonOpen} onClose={() => setNewSeasonOpen(false)} />
    </Layout>
  )
}
